<template>
  <div>
    <div v-if="showStub">
      <div class="col-12">
        <h5 class="no-margin text-bold text-center">
          Please use the Showdigs Agent app to log in
        </h5>
      </div>
      <div class="row justify-center">
        <a
          class="col-xs-6 col-md-6 text-center"
          :href="androidUrl"
          target="_blank"
        >
          <img
            src="/statics/google-play-badge.png"
            alt="Get it on Google Play"
            style="max-width: 150px"
          >
        </a>
        <a
          class="col-xs-6 col-md-6 text-center"
          :href="iosUrl"
          target="_blank"
        >
          <img
            src="/statics/app-store-badge.svg"
            alt="Get it on App Store"
            style="max-width: 135px; margin-top: 10px;"
          >
        </a>
      </div>
    </div>
    <!-- Local Environment: Show traditional login form -->
    <bj-form
      v-else-if="isLocal"
      ref="form"
      :fields="fields"
      :data="payload"
      @keyup.enter.native="submit"
    >
      <div
        slot="start"
        class="col-12"
      >
        <h5 class="no-margin underline-title text-bold">
          Sign in to Showdigs
        </h5>
      </div>
      <div
        slot="end"
        class="col-xs-5 col-md-6"
      >
        <q-btn
          no-caps
          class="full-width"
          rounded
          color="primary"
          label="Sign in"
          @click="submit"
        />
      </div>
      <div
        slot="end"
        class="col-xs-7 col-md-6 text-right"
      >
        <q-btn
          no-caps
          :to="{name: 'auth.forgot-password'}"
          label="Forgot Password?"
          rounded
          flat
        />
      </div>
    </bj-form>
    <!-- Production Environment: Show Google Sign-In -->
    <div
      v-else
      class="column items-center"
    >
      <div class="col-12">
        <h5 class="no-margin underline-title text-bold">
          Sign in to Showdigs
        </h5>
      </div>
      <div
        class="col-12 q-mt-md"
        style="max-width: 360px; width: 100%;"
      >
        <!-- Google Sign-In button container -->
        <div id="google-signin-button" />

        <!-- Loading state -->
        <div
          v-if="isGoogleLoading"
          class="text-center q-pa-md"
        >
          <q-spinner
            color="primary"
            size="2em"
          />
          <div class="q-mt-sm">
            Loading Google Sign-In...
          </div>
        </div>

        <!-- Error state -->
        <div
          v-if="googleError"
          class="text-negative text-center q-pa-md"
        >
          {{ googleError }}
        </div>
      </div>
    </div>
    <bj-inner-loading
      :visible="loading"
      label="Signing in"
    />
  </div>
</template>

<script>

let redirect = '';
export default {
  mixins: [],
  beforeRouteEnter(to, from, next) {
    redirect = to.query.nextPagePath;
    next();
  },
  data() {
    return {
      loading: false,
      fields: [
        {
          name: 'email',
          type: 'email',
          label: 'Email',
          rules: ['required', 'email'],
          error: 'You must fill a valid email address',
        },
        {
          name: 'password',
          type: 'password',
          label: 'Password',
          rules: ['required'],
        },
      ],
      payload: {
        email: '',
        password: '',
        credential: '', // For Google login
      },
      androidUrl: '',
      iosUrl: '',
      showStub: false,
      isGoogleLoading: true,
      googleError: null,
    };
  },
  computed: {
    isLocal() {
      return process && process.env.APP_ENV === 'local';
    },
    googleClientId() {
      return this.$config.googleOAuthClientId;
    },
  },
  created() {
    if (this.$route.query !== undefined && this.$route.query.verified != undefined) {
      this.uiAlert(
        'Sign in to start using Showdigs.',
        'You\'re ready to go!',
      );
    } else if (this.$route.query !== undefined && this.$route.query.expired_invitation === 'true') {
      this.uiAlert('Your invitation link has expired. To generate a new invite contact our team at agents@showdigs.com');
    } else if (this.$route.query !== undefined && this.$route.query.invalid_signature === 'true') {
      this.uiAlert('Invalid verification signature');
    }
    this.logout(false);
    this.$router.push({
      query: {},
    });
  },
  mounted() {
    if (!this.isLocal && !this.showStub) {
      this.initializeGoogleSignIn();
    }
  },
  methods: {
    async initializeGoogleSignIn() {
      try {
        this.isGoogleLoading = true;
        this.googleError = null;

        // Load Google Identity Services script if not already loaded
        if (!window.google || !window.google.accounts.id) {
          await this.loadGoogleScript();
        }

        // Initialize Google Identity Services
        window.google.accounts.id.initialize({
          client_id: this.googleClientId,
          callback: this.handleCredentialResponse,
          scope: 'email',
          hd: 'showdigs.com',
        });

        // Render the Sign-In button
        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: 'sign_in_with',
            shape: 'rectangular',
            logo_alignment: 'left',
          },
        );

        // Show One Tap prompt for returning users (automatic sign-in)
        // This will automatically prompt users who have previously signed in
        window.google.accounts.id.prompt((notification) => {
          // Handle notification if needed (e.g., user dismissed the prompt)
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // One Tap is not displayed or was skipped
            // User can still use the button to sign in
          }
        });

        this.isGoogleLoading = false;
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
        this.googleError = 'Failed to load Google Sign-In. Please refresh the page and try again.';
        this.isGoogleLoading = false;
      }
    },

    loadGoogleScript() {
      return new Promise((resolve, reject) => {
        // Check if script is already loaded
        if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;

        script.onload = () => {
          // Wait for Google API to be available
          const checkGoogle = () => {
            if (window.google && window.google.accounts.id) {
              resolve();
            } else {
              setTimeout(checkGoogle, 100);
            }
          };
          checkGoogle();
        };

        script.onerror = () => {
          reject(new Error('Failed to load Google Identity Services script'));
        };

        document.head.appendChild(script);
      });
    },

    handleCredentialResponse(response) {
      try {
        this.payload.credential = response.credential;

        // Cancel One Tap prompt after receiving credential
        if (window.google && window.google.accounts.id) {
          window.google.accounts.id.cancel();
        }

        // Here you would typically send the credential to your backend
        // for verification and user authentication
        this.submitGoogleLogin();
      } catch (error) {
        console.error('Error handling credential response:', error);
        this.googleError = 'Sign-in failed. Please try again.';
      }
    },

    simpleLogin() {
      const vm = this;

      return new Promise(async (resolve, reject) => {
        try {
          await this.api().request('GET', '../sanctum/csrf-cookie');
        } catch (error) {}

        // Determine login endpoint based on environment and login type
        const endpoint = this.isLocal ? 'auth/spa-login' : 'admin/auth/google';
        const loginPayload = this.isLocal
          ? { email: vm.payload.email, password: vm.payload.password }
          : { credential: vm.payload.credential };

        vm
          .api()
          .request('POST', endpoint, loginPayload)
          .then((user) => {
            if (this.isLocal && user.role === this.$config.roles.AGENT) {
              this.getSettings().then((settings) => {
                settings = settings.map((setting) => ({
                  key: setting.key,
                  value: setting.value,
                  children: setting.children.map((s) => ({
                    key: s.key,
                    value: s.value,
                    market_id: s.market_id,
                  })),
                }));
                this.$store.commit('app/SET_SETTINGS', settings);

                this.androidUrl = `https://play.google.com/store/apps/details?id=${this.getSetting('android_package_name')}`;
                this.iosUrl = `https://itunes.apple.com/us/app/apple-store/id${this.getSetting('app_store_app_id')}?ls=1&mt=8`;
                this.showStub = true;
                reject();
              });
            } else if (this.isLocal && [this.$config.roles.ACCOUNT_OWNER, this.$config.roles.MEMBER].includes(user.role)) {
              this.uiNotify('Please use the PM app at app.showdigs.com', 'negative');
              this.logout(false);
              reject();
            } else {
              this.$store.commit('auth/LOGIN', user);

              resolve(user);
            }
          }).catch((response) => {
            if (this.isLocal && response.resend_verification_email_url) {
              this.$q.notify({
                message: 'Your account is not verified yet',
                icon: 'error_outline',
                color: 'black',
                actions: [
                  {
                    label: 'Resend mail',
                    handler: () => {
                      this.api().request('POST', response.resend_verification_email_url).then(() => {
                        this.uiNotify('Verification email sent.');
                      });
                    },
                  },
                ],
              });
            } else {
              this.uiNotify(
                this.getFirstError(response) || response.message,
                'negative',
              );
            }
            reject(response.error);
          });
      });
    },
    submit() {
      const vm = this;
      if (this.isLocal) {
        this.$refs.form.touch();
        this.$refs.form.validate().then(
          () => {
            this.loading = true;
            this.simpleLogin()
              .then((user) => {
                this.loading = false;
                vm.uiNotify(
                  `Welcome ${
                    user.full_name}`,
                );
                if (!!redirect && redirect !== '/') {
                  vm.$router.push(redirect);
                } else {
                  vm.redirect('app.activities');
                }
              }).catch(() => {
                this.loading = false;
              });
          },
          () => {
            this.uiNotify('Please fill email & password', 'black');
          },
        );
      }
    },
    submitGoogleLogin() {
      const vm = this;
      this.loading = true;
      this.simpleLogin()
        .then((user) => {
          this.loading = false;
          vm.uiNotify(
            `Welcome ${
              user.full_name}`,
          );
          if (!!redirect && redirect !== '/') {
            vm.$router.push(redirect);
          } else {
            vm.redirect('app.activities');
          }
        }).catch(() => {
          this.loading = false;
        });
    },
    getSettings() {
      return this.api().request('GET', 'public/settings');
    },
  },
};
</script>

<style>
</style>
